import { useEffect, useId, useMemo, useState } from "react";
import type { FormEvent } from "react";
import type {
  DeskClient,
  DeskContext,
  DeskState,
  Posting,
  PostingCommand,
  ProtestCommand,
  ProtestKind,
  ProtestRules,
  FilerRole,
  DecisionCommand,
  Protest,
} from "@/api/desk";
import type { HeatRider } from "@/types";
import {
  correctionWindowClosed,
  deadlineLabel,
  kindName,
  money,
  roleName,
  safeAttachment,
  timestamp,
} from "./policy";
import { postingTimeInZone } from "./time";
function getRules(context: DeskContext): ProtestRules {
  if (!context.competition.protest) throw new Error("Desk protest configuration is missing");
  return context.competition.protest;
}
export function PostingForm({
  state,
  context,
  corrected,
  disabled,
  submit,
}: {
  state: DeskState;
  context: DeskContext;
  corrected?: Posting;
  disabled: boolean;
  submit: (body: PostingCommand) => void;
}) {
  const [division, setDivision] = useState(
    corrected?.division_id ??
      state.eligible_scopes.find((s) => s.eligible || s.heats.some((h) => h.eligible))
        ?.division_id ??
      "",
  );
  const [heat, setHeat] = useState(corrected?.heat_id ?? "");
  const scope = state.eligible_scopes.find((s) => s.division_id === division);
  const [kind, setKind] = useState<Posting["kind"]>(corrected ? "corrected" : "provisional");
  const [signatures, setSignatures] = useState(
    corrected?.required_signatures ??
      (context.competition.sanction?.pack === "iwwf_boat_2026" ? 2 : 1),
  );
  const [cut, setCut] = useState(corrected?.cut_line?.toString() ?? "");
  const [posted, setPosted] = useState("");
  const timeHelp = useId();
  const postingTime = useMemo(
    () => postingTimeInZone(posted, context.competition.timezone),
    [posted, context.competition.timezone],
  );
  const latest = (id: string | null) =>
    state.postings
      .filter((p) => p.division_id === division && p.heat_id === id)
      .sort((a, b) => b.posting_no - a.posting_no)[0];
  const scopeAllowed = corrected
    ? latest(heat || null)?.id === corrected.id && !!corrected.correction_opened_at
    : heat
      ? !!scope?.heats.find((h) => h.id === heat)?.eligible && !latest(heat)?.official
      : !!scope?.eligible && !latest(null)?.official;
  const save = (event: FormEvent) => {
    event.preventDefault();
    if (!scopeAllowed || postingTime.error) return;
    submit({
      division_id: division,
      ...(heat ? { heat_id: heat } : {}),
      kind,
      required_signatures: signatures,
      ...(cut ? { cut_line: Number(cut) } : {}),
      ...(postingTime.instant ? { posted_at: postingTime.instant } : {}),
      ...(corrected ? { supersedes_id: corrected.id } : {}),
    });
  };
  return (
    <form onSubmit={save} className="desk-form">
      <p>
        {corrected
          ? `Re-freeze the edited results as v${corrected.posting_no + 1}. v${corrected.posting_no} stays in history.`
          : "Freeze a results snapshot. Official status requires the configured signatures and panel approval."}
      </p>
      <label>
        Division
        <select
          required
          value={division}
          disabled={disabled || !!corrected}
          onChange={(e) => {
            setDivision(e.target.value);
            setHeat("");
          }}
        >
          <option value="">Choose division</option>
          {state.eligible_scopes.map((s) => (
            <option key={s.division_id} value={s.division_id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Result scope
        <select
          value={heat}
          disabled={disabled || !!corrected}
          onChange={(e) => setHeat(e.target.value)}
        >
          <option value="">Whole division</option>
          {scope?.heats.map((h) => (
            <option key={h.id} value={h.id}>
              {h.name}
              {h.eligible ? "" : " — scoring incomplete"}
            </option>
          ))}
        </select>
      </label>
      {!corrected && (
        <label>
          Posting type
          <select
            value={kind}
            disabled={disabled}
            onChange={(e) => setKind(e.target.value as Posting["kind"])}
          >
            <option value="provisional">Provisional</option>
            <option value="official">Request official sign-off</option>
          </select>
        </label>
      )}
      <div className="desk-form-pair">
        <label>
          Required signatures
          <select
            disabled={disabled}
            value={signatures}
            onChange={(e) => setSignatures(Number(e.target.value))}
          >
            {context.competition.sanction?.pack !== "iwwf_boat_2026" && (
              <option value={1}>1 signature</option>
            )}
            <option value={2}>2 signatures</option>
          </select>
        </label>
        <label>
          Cut line
          <input
            type="number"
            min={1}
            value={cut}
            disabled={disabled}
            onChange={(e) => setCut(e.target.value)}
            placeholder="Optional"
          />
        </label>
      </div>
      <label>
        Posting time (optional)
        <input
          type="datetime-local"
          step={1}
          aria-describedby={timeHelp}
          aria-invalid={!!postingTime.error}
          value={posted}
          disabled={disabled}
          onChange={(e) => setPosted(e.target.value)}
        />
      </label>
      <p className="desk-note" id={timeHelp}>
        Enter local time in {context.competition.timezone}. Leave blank to use the server time. A
        custom time cannot precede the last score change.
      </p>
      {postingTime.error && (
        <p className="desk-warning" role="alert">
          {postingTime.error}
        </p>
      )}
      {!scopeAllowed && (
        <p role="status" className="desk-warning">
          {corrected
            ? "A newer posting exists or its correction is no longer open. Review the latest version."
            : "This scope is not ready or already official. Finish scoring or open a correction on its latest posting."}
        </p>
      )}
      <button
        className="desk-primary"
        disabled={disabled || !division || !scopeAllowed || !!postingTime.error}
      >
        {corrected ? "Post corrected results" : "Post results"}
      </button>
    </form>
  );
}
export function FileProtestForm({
  state,
  context,
  client,
  disabled,
  submit,
}: {
  state: DeskState;
  context: DeskContext;
  client: DeskClient;
  disabled: boolean;
  submit: (body: ProtestCommand) => void;
}) {
  const baseRules = getRules(context);
  const heats = state.eligible_scopes.flatMap((s) =>
    s.heats.map((h) => ({ ...h, label: `${s.name} · ${h.name}` })),
  );
  const [kindChoice, setKind] = useState<ProtestKind>(baseRules.kinds[0] ?? "protest");
  const [heat, setHeat] = useState(heats[0]?.id ?? "");
  const [competitor, setCompetitor] = useState("");
  const [filer, setFiler] = useState("");
  const [attempt, setAttempt] = useState("");
  const [riders, setRiders] = useState<HeatRider[]>([]);
  const [attempts, setAttempts] = useState<{ heat_rider_id: string; attempt_number: number }[]>([]);
  const [targetError, setTargetError] = useState("");
  const [targetLoading, setTargetLoading] = useState(false);
  const [targetRevision, setTargetRevision] = useState(0);
  const [roleChoice, setRole] = useState<FilerRole>(baseRules.filers[0] ?? "rider");
  const [name, setName] = useState("");
  const [grounds, setGrounds] = useState("");
  const [attachments, setAttachments] = useState("");
  const [paid, setPaid] = useState(false);
  const [free, setFree] = useState("server");
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const latest = state.postings
    .filter(
      (p) =>
        (p.heat_id === heat ||
          (!p.heat_id &&
            state.eligible_scopes
              .find((s) => s.division_id === p.division_id)
              ?.heats.some((h) => h.id === heat))) &&
        !state.postings.some(
          (other) =>
            other.division_id === p.division_id &&
            other.heat_id === p.heat_id &&
            other.posting_no > p.posting_no,
        ),
    )
    .sort((a, b) => Date.parse(b.posted_at) - Date.parse(a.posted_at))[0];
  const [postingId, setPostingId] = useState("auto");
  const selected = state.postings.find(
    (p) => p.id === (postingId === "auto" ? latest?.id : postingId),
  );
  const rules = selected?.snapshot?.protest_config ?? baseRules;
  const kind = rules.kinds.includes(kindChoice) ? kindChoice : (rules.kinds[0] ?? "protest");
  const role = rules.filers.includes(roleChoice) ? roleChoice : (rules.filers[0] ?? "rider");
  const needsPosting = kind === "correction" || rules.window_from !== "occurrence";
  const needsOfficial =
    kind === "correction"
      ? rules.correction_window_minutes != null && rules.correction_window_from === "official"
      : rules.window_from === "official";
  const selectedIsCurrent =
    !selected ||
    !state.postings.some(
      (other) =>
        other.division_id === selected.division_id &&
        other.heat_id === selected.heat_id &&
        other.posting_no > selected.posting_no,
    );
  const windowReady =
    selectedIsCurrent &&
    (!needsPosting || !!selected) &&
    (!needsOfficial || !!selected?.official_at);
  let windowDescription = "The deadline is measured from the actual attempt end.";
  if (needsOfficial && !selected?.official_at)
    windowDescription = "Official results are required before filing.";
  else if (needsPosting && !selected) windowDescription = "A posting is required for this window.";
  else if (kind === "correction" && selected) {
    const from =
      rules.correction_window_from === "official" ? selected.official_at : selected.posted_at;
    windowDescription =
      rules.correction_window_minutes == null
        ? "No correction time limit."
        : `Correction window: ${deadlineLabel(from ? new Date(Date.parse(from) + rules.correction_window_minutes * 60000).toISOString() : null)}`;
  } else if (rules.window_from !== "occurrence" && selected)
    windowDescription = `${deadlineLabel(selected.protest_deadline_at)}${selected.protest_deadline_at ? ` · ${timestamp(selected.protest_deadline_at, context.competition.timezone)}` : ""}`;

  if (!selectedIsCurrent)
    windowDescription = "A newer posting exists. Select its latest version before filing.";
  useEffect(() => {
    if (!heat) return;
    let alive = true;
    setTargetLoading(true);
    setTargetError("");
    setRiders([]);
    setAttempts([]);
    Promise.all([client.riders(heat), client.attempts(heat)])
      .then(([r, a]) => {
        if (alive) {
          setRiders(r);
          setAttempts(a);
        }
      })
      .catch((e) => {
        if (alive) setTargetError(e instanceof Error ? e.message : "Could not load heat riders");
      })
      .finally(() => {
        if (alive) setTargetLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [heat, client, targetRevision]);
  const links = attachments
    .split(/\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  const badLink = links.some((link) => !safeAttachment(link));
  const overrideAllowed = context.roles.some((r) => ["admin", "chief_judge"].includes(r));
  const manualFree = context.roles.some((r) => ["admin", "head_judge"].includes(r));
  const save = (e: FormEvent) => {
    e.preventDefault();
    if (badLink || targetError || !windowReady || !rules.allowed) return;
    submit({
      kind,
      heat_id: heat,
      ...(selected ? { posting_id: selected.id } : {}),
      ...(competitor ? { heat_rider_id: competitor } : {}),
      ...(attempt ? { attempt_number: Number(attempt) } : {}),
      ...(role === "rider" ? { filer_heat_rider_id: filer || competitor } : {}),
      filed_by_name: name,
      filed_by_role: role,
      grounds,
      fee_paid: paid,
      attachments: links,
      ...(kind === "protest" && rules.free_per_season && manualFree && free !== "server"
        ? { free_protest_used: free === "free" }
        : {}),
      ...(kind === "correction" && overrideAllowed && override
        ? { override: true, override_reason: overrideReason }
        : {}),
    });
  };
  return (
    <form className="desk-form" onSubmit={save}>
      <p>
        File on behalf of an eligible rider or representative. The server checks the rule, deadline
        and fee.
      </p>
      <label>
        Kind
        <select
          value={kind}
          disabled={disabled}
          onChange={(e) => setKind(e.target.value as ProtestKind)}
        >
          {rules.kinds.map((k) => (
            <option key={k} value={k}>
              {kindName[k]}
            </option>
          ))}
        </select>
      </label>
      <label>
        Heat
        <select
          value={heat}
          required
          disabled={disabled}
          onChange={(e) => {
            setHeat(e.target.value);
            setCompetitor("");
            setFiler("");
            setAttempt("");
            setPostingId("auto");
          }}
        >
          <option value="">Choose heat</option>
          {heats.map((h) => (
            <option key={h.id} value={h.id}>
              {h.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        Posting
        <select
          value={postingId === "auto" ? (latest?.id ?? "") : postingId}
          disabled={disabled}
          onChange={(e) => setPostingId(e.target.value)}
        >
          <option value="">No posting</option>
          {state.postings
            .filter(
              (p) =>
                p.heat_id === heat ||
                (!p.heat_id &&
                  state.eligible_scopes
                    .find((s) => s.division_id === p.division_id)
                    ?.heats.some((h) => h.id === heat)),
            )
            .map((p) => (
              <option
                key={p.id}
                value={p.id}
                disabled={state.postings.some(
                  (other) =>
                    other.division_id === p.division_id &&
                    other.heat_id === p.heat_id &&
                    other.posting_no > p.posting_no,
                )}
              >
                v{p.posting_no} · {p.official ? "Official" : "Provisional"}
                {p.heat_id ? "" : " · whole division"}
              </option>
            ))}
        </select>
      </label>
      <p className="desk-note">{windowDescription}</p>
      {targetError && (
        <p className="desk-warning" role="alert">
          {targetError}{" "}
          <button type="button" disabled={disabled} onClick={() => setTargetRevision((n) => n + 1)}>
            Retry riders
          </button>
        </p>
      )}
      <label>
        Competitor
        <select
          value={competitor}
          disabled={disabled || targetLoading}
          required={kind !== "correction" && rules.window_from === "occurrence"}
          onChange={(e) => {
            setCompetitor(e.target.value);
            setAttempt("");
          }}
        >
          <option value="">
            {targetLoading ? "Loading riders…" : "Whole heat / choose competitor"}
          </option>
          {riders.map((r) => (
            <option key={r.id} value={r.id}>
              {r.rider.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Attempt
        <select
          value={attempt}
          disabled={disabled || !competitor}
          required={kind !== "correction" && rules.window_from === "occurrence"}
          onChange={(e) => setAttempt(e.target.value)}
        >
          <option value="">All attempts</option>
          {attempts
            .filter((a) => a.heat_rider_id === competitor)
            .map((a) => (
              <option key={a.attempt_number} value={a.attempt_number}>
                Attempt {a.attempt_number}
              </option>
            ))}
        </select>
      </label>
      <label>
        Filer role
        <select
          value={role}
          disabled={disabled}
          onChange={(e) => setRole(e.target.value as FilerRole)}
        >
          {rules.filers.map((r) => (
            <option key={r} value={r}>
              {roleName[r]}
            </option>
          ))}
        </select>
      </label>
      {role === "rider" && (
        <label>
          Filing competitor
          <select
            value={filer || competitor}
            disabled={disabled || targetLoading}
            required
            onChange={(e) => setFiler(e.target.value)}
          >
            <option value="">Choose competitor in this heat</option>
            {riders.map((r) => (
              <option key={r.id} value={r.id}>
                {r.rider.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label>
        Filer name
        <input
          required
          maxLength={200}
          value={name}
          disabled={disabled}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label>
        Grounds
        <textarea
          required
          maxLength={8000}
          rows={4}
          value={grounds}
          disabled={disabled}
          onChange={(e) => setGrounds(e.target.value)}
        />
      </label>
      <label>
        Attachment links (one per line)
        <textarea
          rows={2}
          value={attachments}
          disabled={disabled}
          onChange={(e) => setAttachments(e.target.value)}
          placeholder="https://…"
        />
      </label>
      {badLink && <p role="alert">Use an HTTP or HTTPS link without embedded credentials.</p>}
      <div className="desk-fee">
        <strong>Rule fee: {money(rules.fee_cents, rules.currency)}</strong>
        <label className="desk-check">
          <input
            type="checkbox"
            checked={paid}
            disabled={disabled}
            onChange={(e) => setPaid(e.target.checked)}
          />
          Fee paid
        </label>
      </div>
      <p className="desk-note">
        {rules.free_per_season
          ? `${rules.free_per_season} free protest(s) per ${rules.free_counter_scope === "wwa_season" ? "WWA season" : rules.free_counter_scope}. Availability is checked on filing; the saved record shows the actual fee.`
          : "No free protests for this rule set."}
      </p>
      {!!rules.free_per_season && manualFree && kind === "protest" && (
        <label>
          Rider's free-protest statement
          <select value={free} disabled={disabled} onChange={(e) => setFree(e.target.value)}>
            <option value="server">Use the recorded counter</option>
            <option value="free">Rider confirms free protest available</option>
            <option value="used">Rider confirms free protest already used</option>
          </select>
        </label>
      )}
      {kind === "correction" && overrideAllowed && (
        <>
          <label className="desk-check">
            <input
              type="checkbox"
              checked={override}
              disabled={disabled}
              onChange={(e) => setOverride(e.target.checked)}
            />
            Chief judge: override correction window
          </label>
          {override && (
            <label>
              Override reason
              <textarea
                required
                maxLength={8000}
                value={overrideReason}
                disabled={disabled}
                onChange={(e) => setOverrideReason(e.target.value)}
              />
            </label>
          )}
        </>
      )}
      <button
        className="desk-primary"
        disabled={
          disabled ||
          targetLoading ||
          !!targetError ||
          badLink ||
          !heat ||
          !windowReady ||
          !rules.allowed ||
          !rules.kinds.includes(kind) ||
          !rules.filers.includes(role)
        }
      >
        File protest
      </button>
    </form>
  );
}
export function CorrectionForm({
  posting,
  context,
  disabled,
  submit,
}: {
  posting: Posting;
  context: DeskContext;
  disabled: boolean;
  submit: (reason: string, override: boolean) => void;
}) {
  const [reason, setReason] = useState("");
  const [override, setOverride] = useState(false);
  const rules = posting.snapshot?.protest_config ?? getRules(context);
  const closed = correctionWindowClosed(posting, rules);
  const allowed = context.roles.some((r) => ["admin", "chief_judge"].includes(r));
  return (
    <form
      className="desk-form"
      onSubmit={(e) => {
        e.preventDefault();
        submit(reason, override);
      }}
    >
      <p>
        Existing results stay in history. Edit the affected scores, then post a corrected version to
        freeze them again.
      </p>
      {closed && (
        <p className="desk-warning">
          Correction window closed. A chief judge must override it with a reason.
        </p>
      )}
      <label>
        Reason for correction
        <textarea
          required
          rows={4}
          maxLength={8000}
          value={reason}
          disabled={disabled}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      {allowed && (
        <label className="desk-check">
          <input
            type="checkbox"
            checked={override}
            disabled={disabled}
            onChange={(e) => setOverride(e.target.checked)}
          />
          Override correction window as chief judge
        </label>
      )}
      <button className="desk-primary" disabled={disabled || (closed && !override)}>
        Open correction
      </button>
    </form>
  );
}
export function DecisionForm({
  protest,
  disabled,
  submit,
}: {
  protest: Protest;
  disabled: boolean;
  submit: (body: DecisionCommand) => void;
}) {
  const [status, setStatus] = useState<DecisionCommand["status"]>("upheld");
  const [decision, setDecision] = useState("");
  return (
    <form
      className="desk-form"
      onSubmit={(e) => {
        e.preventDefault();
        submit({
          status,
          decision,
          ...(status === "upheld" ? { outcome: "rescore" as const } : {}),
        });
      }}
    >
      <p className="desk-eyebrow">{kindName[protest.kind]}</p>
      <p>
        Filed by {protest.filed_by_name}
        {protest.attempt_number ? ` · Attempt ${protest.attempt_number}` : ""}
      </p>
      <p>{protest.grounds}</p>
      <label>
        Decision status
        <select
          value={status}
          disabled={disabled}
          onChange={(e) => setStatus(e.target.value as DecisionCommand["status"])}
        >
          <option value="upheld">Upheld</option>
          <option value="dismissed">Dismissed</option>
          <option value="invalid">Invalid</option>
          <option value="withdrawn">Withdrawn</option>
        </select>
      </label>
      <label>
        Decision reason
        <textarea
          required
          rows={4}
          maxLength={8000}
          value={decision}
          disabled={disabled}
          onChange={(e) => setDecision(e.target.value)}
        />
      </label>
      {status === "upheld" && (
        <p className="desk-note">
          An upheld protest opens a correction. A used free protest is restored; any required fee
          refund is recorded in the decision.
        </p>
      )}
      <button className="desk-primary" disabled={disabled}>
        Save decision
      </button>
    </form>
  );
}
